import React, { useRef, useEffect } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'

// Hooks
import { useScrolledAnimation } from '../useScrolledAnimation'

// Styles
import {
  AnimateSlideInMask,
  AnimateSlideInOuter,
  AnimateSlideInInner,
  AnimateSlideInContent,
} from './index.style'

const AnimateSlideIn = ({
  id,
  duration,
  delay,
  scale,
  children,
  useInView,
  isInView,
  syncWithScroll,
  debug,
}) => {
  const outerRef = useRef(null),
    innerRef = useRef(null),
    contentRef = useRef(null),
    maskIn = useRef(null)

  useScrolledAnimation({
    id: id,
    transition: maskIn,
    sync: syncWithScroll,
    delay: delay,
    useInView: useInView,
    isInView: isInView,
    debug: debug,
  })

  useEffect(() => {
    if (!outerRef.current || !innerRef.current || !contentRef.current) return

    maskIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: duration,
        ease: syncWithScroll ? 'power1.out' : 'power2.inOut',
      },
    })

    maskIn.current.fromTo(
      outerRef.current,
      {
        yPercent: '105',
      },
      {
        ease: 'power2.inOut',
        duration: 1.5,
        yPercent: 0,
      },
      0
    )

    maskIn.current.fromTo(
      innerRef.current,
      {
        yPercent: '-105',
      },
      {
        ease: 'power2.inOut',
        duration: 1.5,
        yPercent: 0,
      },
      0
    )

    maskIn.current.fromTo(
      contentRef.current,
      {
        scale: scale,
      },
      {
        ease: 'power2.inOut',
        duration: 1.5,
        scale: 1,
      },
      0
    )
  }, [outerRef, innerRef, contentRef, duration, scale, syncWithScroll])

  return (
    <AnimateSlideInMask data-scroll data-scroll-id={id}>
      <AnimateSlideInOuter ref={outerRef}>
        <AnimateSlideInInner ref={innerRef}>
          <AnimateSlideInContent ref={contentRef}>
            {children}
          </AnimateSlideInContent>
        </AnimateSlideInInner>
      </AnimateSlideInOuter>
    </AnimateSlideInMask>
  )
}

AnimateSlideIn.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  duration: PropTypes.number,
  scale: PropTypes.number,
  useInView: PropTypes.bool,
  isInView: PropTypes.bool,
  syncWithScroll: PropTypes.bool,
  debug: PropTypes.bool,
}

AnimateSlideIn.defaultProps = {
  delay: 0,
  duration: 1.5,
  scale: 1,
  useInView: true,
  isInView: false,
  syncWithScroll: false,
}

export default AnimateSlideIn
